/**
 * Bold and easy coloring — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_coloring`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const boldEasyColoringPack: ToilePack = {
    slug: 'bold-easy-coloring',
    category: 'coloring',
    name: 'Bold and easy coloring',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'flower', label: 'One flower', subject: 'a single large daisy, a round center and eight broad petals, two wide leaves on a stem'},
        {key: 'mug', label: 'Coffee mug', subject: 'a wide mug of coffee with a big handle, two curls of steam and one round cookie'},
        {key: 'sun', label: 'Smiling sun', subject: 'a smiling sun with thick triangular rays and one small round cloud drifting past'},
    ],
    styles: [
        {key: 'alone', label: 'One shape', style: 'the object alone at maximum size, very thick uniform outlines, no more than six closed shapes', palette: 'black ink only', framing: 'The single shape filling almost the whole page, a wide white margin around it'},
        {key: 'scene', label: 'Little scene', style: 'the object on a ground line with two large companion shapes, the same heavy outline weight throughout', palette: 'black ink only', framing: 'The scene on a baseline across the lower page, open white paper above'},
        {key: 'border', label: 'Bordered', style: 'the object drawn large inside a thick decorative border of big repeating shapes, equal outline weight throughout', palette: 'black ink only', framing: 'The border ruled around the page edge, the object centered inside it'},
    ],
    directions: ['a butterfly instead', "for a child's birthday card", 'as a set of four'],
    seo: {
        title: 'Bold and easy coloring pages: flower, mug, sun',
        metaDescription: 'Nine bold and easy coloring pages — a flower, a mug, a sun — drawn alone, in a scene or bordered, thick outlines, one sitting to finish.',
        h1: 'Bold and easy coloring pages: flower, mug and sun in three styles',
        tagline: 'Nine thick-outline pages, three subjects, three styles each — built for markers and a ten-minute finish.',
        intro: 'Bold and easy is the style for tired hands and quick wins: thick black outlines, big open areas, few pieces. A daisy, a coffee mug, a smiling sun — each drawn alone at full size, set in a small scene, or framed inside a bordered panel. Every page finishes in one sitting and looks good in any color. Print it, or ask for a different simple object.',
        useCases: [
            'a ten-minute wind-down',
            "a seniors' coloring group",
            'a first coloring page for a beginner',
            'a marker-friendly practice sheet',
        ],
        faq: [
            {q: 'What size do the pages print at?', a: 'Each page is drawn at portrait A4 proportions (832 by 1216) with a white margin, so it prints clean on standard paper.'},
            {q: 'Do the thick outlines hold up at any size?', a: 'Yes — the lines are drawn heavy and even, so they stay bold printed small or filling the page.'},
            {q: 'Can I get a different simple object?', a: 'Yes — that is what the direction field on the page is for. Type a different object and the page is drawn again in the same three styles.'},
        ],
    },
}
